import { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '@/pages/admin/components/AdminLayout';
import { supabase } from '@/lib/supabase';
import ImageUploader from '@/components/base/ImageUploader';

const ANNOUNCEMENT_EDGE_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/announcements-admin';

interface AnnouncementItem {
  id: string;
  title: string;
  type: string;
  is_active: boolean;
  sort_order: number;
}

// ─── Types ────────────────────────────────────────────────────────────────────

type ComponentType = 'banner' | 'category' | 'benefit' | 'midBanner' | 'bottomBanner' | 'pointsCard' | 'announcement' | 'productSection';

interface PageComponent {
  id: string;
  type: ComponentType;
  visible: boolean;
}

export interface BannerItem {
  id: string;
  tag: string;
  title: string;
  subtitle: string;
  cta: string;
  image: string;
  productId: string;
}

export interface CategoryItem {
  icon: string;
  label: string;
  color: string;
  iconColor: string;
  path: string;
}

export interface BenefitItem {
  icon: string;
  title: string;
  desc: string;
}

export interface ProductSectionItem {
  id: string;
  title: string;
  subtitle: string;
  displayStyle: 'grid' | 'list';
  productIds: string[];
}

export interface HomeConfig {
  themeColor: string;
  storeName: string;
  logoImage: string;
  logoBgGradient: string;
  banners: BannerItem[];
  categories: CategoryItem[];
  benefits: BenefitItem[];
  midBanner: { title: string; subtitle: string; tag: string; image: string; path: string };
  bottomBanner: { title: string; subtitle: string; image: string; path: string };
  pointsCardGradient: string;
  pageLayout: PageComponent[];
  productSections: ProductSectionItem[];
}

interface DBProduct {
  id: string;
  name: string;
  price: number;
  original_price: number | null;
  images: string[];
  category: string;
  sales: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const THEME_OPTIONS = [
  { value: 'emerald', label: '翠绿', from: '#059669', to: '#10b981' },
  { value: 'rose', label: '玫瑰红', from: '#e11d48', to: '#f43f5e' },
  { value: 'amber', label: '琥珀金', from: '#d97706', to: '#f59e0b' },
  { value: 'sky', label: '天空蓝', from: '#0284c7', to: '#38bdf8' },
  { value: 'teal', label: '青碧', from: '#0d9488', to: '#14b8a6' },
  { value: 'indigo', label: '靛蓝', from: '#4338ca', to: '#6366f1' },
];

const GRADIENT_OPTIONS = [
  { value: 'from-emerald-600 to-emerald-500', label: '翠绿渐变', from: '#059669', to: '#10b981' },
  { value: 'from-rose-500 to-orange-400', label: '玫瑰橙', from: '#f43f5e', to: '#fb923c' },
  { value: 'from-amber-500 to-yellow-400', label: '金黄渐变', from: '#f59e0b', to: '#facc15' },
  { value: 'from-teal-600 to-cyan-400', label: '青碧渐变', from: '#0d9488', to: '#22d3ee' },
  { value: 'from-slate-700 to-slate-500', label: '深灰渐变', from: '#334155', to: '#64748b' },
  { value: 'from-indigo-600 to-violet-500', label: '靛紫渐变', from: '#4338ca', to: '#8b5cf6' },
];

const ICON_OPTIONS = [
  'ri-drop-line', 'ri-heart-pulse-line', 'ri-tools-line', 'ri-gift-2-line',
  'ri-capsule-line', 'ri-store-2-line', 'ri-leaf-line', 'ri-star-line',
  'ri-fire-line', 'ri-shield-check-line', 'ri-award-line', 'ri-test-tube-line',
  'ri-scissors-line', 'ri-magic-line', 'ri-flask-line', 'ri-seedling-line',
];

const COLOR_PAIRS = [
  { color: 'bg-emerald-50', iconColor: 'text-emerald-600', label: '翠绿' },
  { color: 'bg-rose-50', iconColor: 'text-rose-500', label: '玫瑰' },
  { color: 'bg-amber-50', iconColor: 'text-amber-500', label: '琥珀' },
  { color: 'bg-violet-50', iconColor: 'text-violet-500', label: '紫罗兰' },
  { color: 'bg-sky-50', iconColor: 'text-sky-500', label: '天蓝' },
  { color: 'bg-gray-100', iconColor: 'text-gray-500', label: '灰色' },
  { color: 'bg-teal-50', iconColor: 'text-teal-600', label: '青碧' },
  { color: 'bg-orange-50', iconColor: 'text-orange-500', label: '橙色' },
];

const COMPONENT_META: Record<ComponentType, { label: string; icon: string; desc: string; color: string }> = {
  banner: { label: '轮播 Banner', icon: 'ri-image-line', desc: '顶部轮播图', color: 'bg-rose-50 text-rose-500' },
  pointsCard: { label: '积分卡片', icon: 'ri-bank-card-line', desc: '用户积分展示', color: 'bg-amber-50 text-amber-500' },
  benefit: { label: '品牌亮点', icon: 'ri-award-line', desc: '核心优势展示', color: 'bg-emerald-50 text-emerald-600' },
  category: { label: '商品分类', icon: 'ri-grid-line', desc: '分类导航入口', color: 'bg-violet-50 text-violet-500' },
  midBanner: { label: '中部海报', icon: 'ri-layout-masonry-line', desc: '促销宣传横幅', color: 'bg-sky-50 text-sky-500' },
  bottomBanner: { label: '底部海报', icon: 'ri-share-line', desc: '邀请/活动横幅', color: 'bg-teal-50 text-teal-600' },
  announcement: { label: '公告栏', icon: 'ri-notification-3-line', desc: '店铺公告滚动', color: 'bg-orange-50 text-orange-500' },
  productSection: { label: '商品展示', icon: 'ri-shopping-bag-line', desc: '手动选品展示', color: 'bg-pink-50 text-pink-500' },
};

const DEFAULT_LAYOUT: PageComponent[] = [
  { id: 'banner', type: 'banner', visible: true },
  { id: 'pointsCard', type: 'pointsCard', visible: true },
  { id: 'benefit', type: 'benefit', visible: true },
  { id: 'announcement', type: 'announcement', visible: true },
  { id: 'category', type: 'category', visible: true },
  { id: 'midBanner', type: 'midBanner', visible: true },
  { id: 'bottomBanner', type: 'bottomBanner', visible: true },
];

// 仅补充缺失的组件，不改变已有组件的顺序
// 所有组件都可以自由拖动，顺序完全由用户决定
const ensureFixedComponents = (layout: PageComponent[]): PageComponent[] => {
  const allFixed: PageComponent[] = [
    { id: 'banner', type: 'banner', visible: true },
    { id: 'pointsCard', type: 'pointsCard', visible: true },
    { id: 'benefit', type: 'benefit', visible: true },
    { id: 'announcement', type: 'announcement', visible: true },
    { id: 'category', type: 'category', visible: true },
    { id: 'midBanner', type: 'midBanner', visible: true },
    { id: 'bottomBanner', type: 'bottomBanner', visible: true },
  ];
  const result = [...layout];
  // 只追加缺失的，不改变现有顺序
  allFixed.forEach((f) => {
    if (!result.find((c) => c.id === f.id)) {
      result.push(f);
    }
  });
  return result;
};

const DEFAULT_CONFIG: HomeConfig = {
  themeColor: 'emerald',
  storeName: '发之源',
  logoImage: '',
  logoBgGradient: 'from-emerald-500 to-emerald-400',
  banners: [
    {
      id: '1', tag: '明星产品', title: '生姜防脱\n固发精华',
      subtitle: '深层滋养头皮 · 告别脱发烦恼', cta: '立即兑换',
      image: '',
      productId: 'a1000001-0000-0000-0000-000000000001',
    },
    {
      id: '2', tag: '爆款套装', title: '防脱全套\n护发方案',
      subtitle: '洗发 + 精华 + 按摩梳 三重呵护', cta: '查看套装',
      image: '',
      productId: 'a1000001-0000-0000-0000-000000000006',
    },
  ],
  categories: [
    { icon: 'ri-drop-line', label: '洗发护发', color: 'bg-emerald-50', iconColor: 'text-emerald-600', path: '/shop?category=洗发护发' },
    { icon: 'ri-heart-pulse-line', label: '头皮护理', color: 'bg-rose-50', iconColor: 'text-rose-500', path: '/shop?category=头皮护理' },
    { icon: 'ri-tools-line', label: '护发工具', color: 'bg-amber-50', iconColor: 'text-amber-500', path: '/shop?category=护发工具' },
    { icon: 'ri-gift-2-line', label: '套装礼盒', color: 'bg-violet-50', iconColor: 'text-violet-500', path: '/shop?category=套装礼盒' },
    { icon: 'ri-capsule-line', label: '口服营养', color: 'bg-sky-50', iconColor: 'text-sky-500', path: '/shop?category=口服营养' },
    { icon: 'ri-store-2-line', label: '全部商品', color: 'bg-gray-100', iconColor: 'text-gray-500', path: '/shop' },
  ],
  benefits: [
    { icon: 'ri-leaf-line', title: '纯植物配方', desc: '天然草本精华' },
    { icon: 'ri-test-tube-line', title: '科学研发', desc: '临床验证有效' },
    { icon: 'ri-shield-check-line', title: '安全无害', desc: '无添加无刺激' },
    { icon: 'ri-award-line', title: '品质保证', desc: '正品溯源保障' },
  ],
  midBanner: {
    title: '防脱套装礼盒', subtitle: '三重护发 · 全面解决脱发问题',
    tag: '限时特惠',
    image: '',
    path: '/shop?category=套装礼盒',
  },
  bottomBanner: {
    title: '邀好友 · 赚积分', subtitle: '每邀一人奖励 500 积分',
    image: '',
    path: '/community',
  },
  pointsCardGradient: 'from-emerald-600 to-emerald-500',
  pageLayout: DEFAULT_LAYOUT,
  productSections: [],
};

const LOGO_GRADIENT_OPTIONS = [
  { value: 'from-emerald-500 to-emerald-400', label: '翠绿', from: '#10b981', to: '#34d399' },
  { value: 'from-rose-500 to-rose-400', label: '玫瑰红', from: '#f43f5e', to: '#fb7185' },
  { value: 'from-amber-500 to-amber-400', label: '琥珀金', from: '#f59e0b', to: '#fbbf24' },
  { value: 'from-teal-500 to-teal-400', label: '青碧', from: '#14b8a6', to: '#2dd4bf' },
  { value: 'from-slate-600 to-slate-500', label: '深灰', from: '#475569', to: '#64748b' },
  { value: 'from-orange-500 to-orange-400', label: '橙色', from: '#f97316', to: '#fb923c' },
];

// ─── Small helpers ────────────────────────────────────────────────────────────

const TextInput = ({ value, onChange, placeholder, multiline }: {
  value: string; onChange: (v: string) => void; placeholder?: string; multiline?: boolean;
}) => multiline ? (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={2}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-rose-400 resize-none"
  />
) : (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-rose-400"
  />
);

const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="mb-4 last:mb-0">
    <label className="block text-xs text-gray-500 mb-1.5 font-medium">{label}</label>
    {children}
  </div>
);

// ─── Product Picker Modal ─────────────────────────────────────────────────────

const ProductPickerModal = ({
  selectedIds,
  onConfirm,
  onClose,
}: {
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
  onClose: () => void;
}) => {
  const [products, setProducts] = useState<DBProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>(selectedIds);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('products')
        .select('id, name, price, original_price, images, category, sales')
        .eq('status', '上架')
        .order('sort_order', { ascending: true });
      setProducts((data as DBProduct[]) || []);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    setPicked((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl w-[640px] max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">选择商品</h3>
            <p className="text-xs text-gray-400 mt-0.5">已选 {picked.length} 件商品</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <i className="ri-search-line text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索商品名称或分类..."
              className="flex-1 bg-transparent text-sm text-gray-700 focus:outline-none"
            />
          </div>
        </div>

        {/* Product list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-400">
              <i className="ri-shopping-bag-line text-3xl mb-2" />
              <p className="text-sm">暂无商品</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {filtered.map((p) => {
                const isSelected = picked.includes(p.id);
                return (
                  <div
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected ? 'border-rose-400 bg-rose-50' : 'border-gray-100 hover:border-gray-200 bg-white'
                    }`}
                  >
                    <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-gray-300 text-xl" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight">{p.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{p.category}</p>
                      <p className="text-sm font-bold text-rose-500 mt-1">¥{p.price}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border-2 transition-all ${
                      isSelected ? 'bg-rose-500 border-rose-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <i className="ri-check-line text-white" style={{ fontSize: '10px' }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
          <button onClick={() => setPicked([])} className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap">
            清空选择
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              取消
            </button>
            <button
              onClick={() => { onConfirm(picked); onClose(); }}
              className="px-5 py-2 rounded-lg bg-rose-500 text-white text-sm font-medium hover:bg-rose-600 cursor-pointer whitespace-nowrap"
            >
              确认选择 ({picked.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Preview Components ───────────────────────────────────────────────────────

const PreviewBanner = ({ config, selected }: { config: HomeConfig; selected: boolean }) => (
  <div className={`relative transition-all ${selected ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`} style={{ height: '160px' }}>
    {config.banners[0]?.image ? (
      <img src={config.banners[0].image} alt="banner" className="w-full h-full object-cover object-top" />
    ) : (
      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
        <i className="ri-image-line text-gray-400 text-2xl" />
      </div>
    )}
    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
    <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">
      <span className="inline-block bg-white/20 text-white px-2 py-0.5 rounded-full mb-1 border border-white/30" style={{ fontSize: '9px' }}>
        {config.banners[0]?.tag}
      </span>
      <p className="text-white font-bold leading-tight whitespace-pre-line" style={{ fontSize: '13px' }}>
        {config.banners[0]?.title}
      </p>
      <p className="text-white/80 mt-0.5 mb-1.5" style={{ fontSize: '9px' }}>{config.banners[0]?.subtitle}</p>
      <span className="bg-white text-gray-900 font-semibold px-3 py-1 rounded-full" style={{ fontSize: '9px' }}>
        {config.banners[0]?.cta}
      </span>
    </div>
    <div className="absolute bottom-1.5 left-0 right-0 flex justify-center gap-1">
      {config.banners.map((_, i) => (
        <div key={i} className={`rounded-full ${i === 0 ? 'w-3 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50'}`} />
      ))}
    </div>
  </div>
);

const PreviewPointsCard = ({ config, selected }: { config: HomeConfig; selected: boolean }) => (
  <div className={`px-2.5 mt-2 transition-all ${selected ? 'ring-2 ring-rose-500 ring-offset-1 rounded-xl' : ''}`}>
    <div className={`bg-gradient-to-r ${config.pointsCardGradient} rounded-xl p-3`}>
      <p className="text-white/70" style={{ fontSize: '9px' }}>我的积分</p>
      <p className="text-white font-bold text-lg">1,280</p>
      <div className="flex gap-1.5 mt-1">
        <span className="bg-white/20 text-white px-2 py-0.5 rounded-full" style={{ fontSize: '9px' }}>去兑换</span>
        <span className="bg-white/20 text-white px-2 py-0.5 rounded-full" style={{ fontSize: '9px' }}>转积分</span>
      </div>
    </div>
  </div>
);

const PreviewBenefit = ({ config, selected }: { config: HomeConfig; selected: boolean }) => (
  <div className={`bg-white mt-2 mx-2.5 rounded-xl px-3 py-2.5 transition-all ${selected ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`}>
    <div className="grid grid-cols-4 gap-1">
      {config.benefits.slice(0, 4).map((b, i) => (
        <div key={i} className="flex flex-col items-center gap-1">
          <div className="w-6 h-6 flex items-center justify-center bg-emerald-50 rounded-lg">
            <i className={`${b.icon} text-emerald-600`} style={{ fontSize: '11px' }} />
          </div>
          <p className="text-center font-semibold text-gray-800" style={{ fontSize: '8px' }}>{b.title}</p>
          <p className="text-center text-gray-400" style={{ fontSize: '7px' }}>{b.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const PreviewCategory = ({ config, selected }: { config: HomeConfig; selected: boolean }) => (
  <div className={`bg-white mt-2 px-2.5 py-2.5 transition-all ${selected ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`}>
    <p className="font-bold text-gray-900 mb-2" style={{ fontSize: '10px' }}>商品分类</p>
    <div className="grid grid-cols-3 gap-1.5">
      {config.categories.slice(0, 6).map((cat, i) => (
        <div key={i} className={`flex items-center gap-1.5 px-2 py-2 rounded-lg ${cat.color}`}>
          <i className={`${cat.icon} ${cat.iconColor}`} style={{ fontSize: '12px' }} />
          <span className="text-gray-700 font-medium" style={{ fontSize: '8px' }}>{cat.label}</span>
        </div>
      ))}
    </div>
  </div>
);

const PreviewMidBanner = ({ config, selected }: { config: HomeConfig; selected: boolean }) => (
  <div className={`mt-2 mx-2.5 rounded-xl overflow-hidden relative transition-all ${selected ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`} style={{ height: '80px' }}>
    {config.midBanner.image ? (
      <img src={config.midBanner.image} alt="mid" className="w-full h-full object-cover object-top" />
    ) : (
      <div className="w-full h-full bg-gray-200" />
    )}
    <div className="absolute inset-0 bg-black/40 flex flex-col justify-center px-3">
      <span className="text-white/80 mb-0.5" style={{ fontSize: '8px' }}>{config.midBanner.tag}</span>
      <p className="text-white font-bold" style={{ fontSize: '11px' }}>{config.midBanner.title}</p>
      <p className="text-white/70" style={{ fontSize: '8px' }}>{config.midBanner.subtitle}</p>
    </div>
  </div>
);

const PreviewBottomBanner = ({ config, selected }: { config: HomeConfig; selected: boolean }) => (
  <div className={`mt-2 mx-2.5 rounded-xl overflow-hidden relative transition-all ${selected ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`} style={{ height: '70px' }}>
    {config.bottomBanner.image ? (
      <img src={config.bottomBanner.image} alt="bottom" className="w-full h-full object-cover object-top" />
    ) : (
      <div className="w-full h-full bg-gray-200" />
    )}
    <div className="absolute inset-0 bg-black/40 flex flex-col justify-center px-3">
      <p className="text-white font-bold" style={{ fontSize: '11px' }}>{config.bottomBanner.title}</p>
      <p className="text-white/70" style={{ fontSize: '8px' }}>{config.bottomBanner.subtitle}</p>
    </div>
  </div>
);

const ANN_TYPE_OPTIONS = [
  { value: 'info', label: '普通', icon: 'ri-megaphone-line', color: 'text-emerald-600 bg-emerald-50' },
  { value: 'promo', label: '促销', icon: 'ri-gift-line', color: 'text-amber-600 bg-amber-50' },
  { value: 'warning', label: '提醒', icon: 'ri-alarm-warning-line', color: 'text-rose-600 bg-rose-50' },
];

const ANN_TYPE_PREVIEW: Record<string, { icon: string; bg: string; text: string }> = {
  promo: { icon: 'ri-gift-line', bg: 'bg-amber-50', text: 'text-amber-700' },
  warning: { icon: 'ri-alarm-warning-line', bg: 'bg-rose-50', text: 'text-rose-600' },
  info: { icon: 'ri-megaphone-line', bg: 'bg-emerald-50', text: 'text-emerald-700' },
};

const AnnouncementEditor = () => {
  const [list, setList] = useState<AnnouncementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingNew, setAddingNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('info');
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ANNOUNCEMENT_EDGE_URL);
      const json = await res.json();
      setList((json.data as AnnouncementItem[]) ?? []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      await fetch(ANNOUNCEMENT_EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim(), type: newType, sort_order: list.length }),
      });
      setNewTitle('');
      setNewType('info');
      setAddingNew(false);
      fetchList();
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item: AnnouncementItem) => {
    if (togglingId === item.id) return;
    setTogglingId(item.id);
    try {
      await fetch(ANNOUNCEMENT_EDGE_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, is_active: !item.is_active }),
      });
      setList((prev) => prev.map((a) => a.id === item.id ? { ...a, is_active: !a.is_active } : a));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await fetch(ANNOUNCEMENT_EDGE_URL, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      setList((prev) => prev.filter((a) => a.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveEdit = async (id: string) => {
    if (!editTitle.trim()) return;
    setSaving(true);
    try {
      await fetch(ANNOUNCEMENT_EDGE_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, title: editTitle.trim() }),
      });
      setList((prev) => prev.map((a) => a.id === id ? { ...a, title: editTitle.trim() } : a));
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-400 mb-3">启用的公告将在首页滚动展示</p>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="w-5 h-5 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2 mb-3">
          {list.length === 0 && !addingNew && (
            <div className="text-center py-4 text-gray-400 text-xs bg-gray-50 rounded-xl">
              暂无公告，点击下方添加
            </div>
          )}
          {list.map((item) => {
            const typeCfg = ANN_TYPE_OPTIONS.find((t) => t.value === item.type) ?? ANN_TYPE_OPTIONS[0];
            const isEditing = editingId === item.id;
            return (
              <div key={item.id} className={`border rounded-xl p-3 transition-all ${item.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      maxLength={100}
                      autoFocus
                      className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-800 focus:outline-none focus:border-rose-400"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingId(null)}
                        className="flex-1 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap"
                      >
                        取消
                      </button>
                      <button
                        onClick={() => handleSaveEdit(item.id)}
                        disabled={saving || !editTitle.trim()}
                        className="flex-1 py-1.5 rounded-lg bg-rose-500 text-white text-xs cursor-pointer hover:bg-rose-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        {saving ? '保存中...' : '保存'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <div className={`w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${typeCfg.color}`}>
                      <i className={`${typeCfg.icon} text-xs`} />
                    </div>
                    <p className="flex-1 text-xs text-gray-700 leading-relaxed min-w-0 break-words">{item.title}</p>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                      <button
                        onClick={() => { setEditingId(item.id); setEditTitle(item.title); }}
                        className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-emerald-500 cursor-pointer rounded"
                      >
                        <i className="ri-edit-line text-xs" />
                      </button>
                      <button
                        onClick={() => handleToggle(item)}
                        disabled={togglingId === item.id}
                        className={`relative w-8 h-4 rounded-full transition-colors cursor-pointer flex-shrink-0 disabled:opacity-60 ${item.is_active ? 'bg-emerald-500' : 'bg-gray-200'}`}
                      >
                        {togglingId === item.id ? (
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span className="w-2.5 h-2.5 border border-white border-t-transparent rounded-full animate-spin" />
                          </span>
                        ) : (
                          <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${item.is_active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={deletingId === item.id}
                        className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-rose-500 cursor-pointer rounded disabled:opacity-50"
                      >
                        {deletingId === item.id
                          ? <span className="w-2.5 h-2.5 border border-rose-400 border-t-transparent rounded-full animate-spin" />
                          : <i className="ri-delete-bin-line text-xs" />
                        }
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {addingNew ? (
        <div className="border-2 border-rose-200 rounded-xl p-3 bg-rose-50/30 space-y-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="输入公告内容..."
            maxLength={100}
            autoFocus
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs text-gray-800 focus:outline-none focus:border-rose-400 bg-white"
          />
          <div className="flex gap-1.5">
            {ANN_TYPE_OPTIONS.map((t) => (
              <button
                key={t.value}
                onClick={() => setNewType(t.value)}
                className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg border cursor-pointer transition-all text-xs ${newType === t.value ? 'border-rose-400 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
              >
                <i className={t.icon} />
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setAddingNew(false); setNewTitle(''); }}
              className="flex-1 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={handleAdd}
              disabled={saving || !newTitle.trim()}
              className="flex-1 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-medium cursor-pointer hover:bg-rose-600 disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? '添加中...' : '确认添加'}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAddingNew(true)}
          className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-xs text-gray-400 hover:border-rose-300 hover:text-rose-400 cursor-pointer transition-all flex items-center justify-center gap-1.5 whitespace-nowrap"
        >
          <i className="ri-add-line" />
          添加公告
        </button>
      )}
    </div>
  );
};

const PreviewAnnouncement = ({ announcements, selected }: { announcements: AnnouncementItem[]; selected: boolean }) => {
  const active = announcements.filter((a) => a.is_active);
  const ann = active[0];
  const cfg = ann ? (ANN_TYPE_PREVIEW[ann.type] ?? ANN_TYPE_PREVIEW.info) : ANN_TYPE_PREVIEW.info;

  return (
    <div className={`mt-2 mx-2.5 rounded-lg px-3 py-2 flex items-center gap-2 transition-all ${cfg.bg} ${selected ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`}>
      <i className={`${cfg.icon} ${cfg.text}`} style={{ fontSize: '11px' }} />
      <p className={`truncate ${cfg.text}`} style={{ fontSize: '9px' }}>
        {ann ? ann.title : '暂无启用的公告'}
      </p>
      {active.length > 1 && (
        <span className="ml-auto flex-shrink-0 text-gray-400" style={{ fontSize: '8px' }}>+{active.length - 1}</span>
      )}
    </div>
  );
};

const PreviewProductSection = ({
  section,
  allProducts,
  selected,
}: {
  section: ProductSectionItem;
  allProducts: DBProduct[];
  selected: boolean;
}) => {
  const sectionProducts = section.productIds
    .map((id) => allProducts.find((p) => p.id === id))
    .filter(Boolean) as DBProduct[];

  const isLoadingProducts = section.productIds.length > 0 && allProducts.length === 0;

  return (
    <div className={`mt-2 bg-white transition-all ${selected ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`}>
      {/* Section header */}
      <div className="px-2.5 pt-2.5 pb-1.5 flex items-center justify-between">
        <div>
          <p className="font-bold text-gray-900" style={{ fontSize: '11px' }}>{section.title || '商品展示'}</p>
          {section.subtitle && (
            <p className="text-gray-400" style={{ fontSize: '8px' }}>{section.subtitle}</p>
          )}
        </div>
        <span className="text-gray-400" style={{ fontSize: '8px' }}>查看更多 &gt;</span>
      </div>

      {isLoadingProducts ? (
        <div className="mx-2.5 mb-2.5 grid grid-cols-2 gap-1.5">
          {section.productIds.slice(0, 4).map((id) => (
            <div key={id} className="bg-gray-100 rounded-xl animate-pulse" style={{ height: '100px' }} />
          ))}
        </div>
      ) : sectionProducts.length === 0 ? (
        <div className="mx-2.5 mb-2.5 bg-gray-50 rounded-xl flex flex-col items-center justify-center py-4">
          <i className="ri-shopping-bag-line text-gray-300 text-xl mb-1" />
          <p className="text-gray-400" style={{ fontSize: '8px' }}>暂未选择商品</p>
        </div>
      ) : section.displayStyle === 'list' ? (
        <div className="px-2.5 pb-2.5 space-y-1.5">
          {sectionProducts.slice(0, 4).map((p) => (
            <div key={p.id} className="flex items-center gap-2 bg-gray-50 rounded-lg p-1.5">
              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                {p.images?.[0] && (
                  <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-800 font-medium truncate" style={{ fontSize: '8px' }}>{p.name}</p>
                <p className="text-rose-500 font-bold mt-0.5" style={{ fontSize: '9px' }}>¥{p.price}</p>
              </div>
              <span className="bg-rose-500 text-white px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ fontSize: '7px' }}>购买</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-2.5 pb-2.5 grid grid-cols-2 gap-1.5">
          {sectionProducts.slice(0, 4).map((p) => (
            <div key={p.id} className="bg-gray-50 rounded-xl overflow-hidden">
              <div className="w-full bg-gray-200" style={{ height: '70px' }}>
                {p.images?.[0] && (
                  <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top" />
                )}
              </div>
              <div className="p-1.5">
                <p className="text-gray-800 font-medium line-clamp-2 leading-tight" style={{ fontSize: '8px' }}>{p.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-rose-500 font-bold" style={{ fontSize: '9px' }}>¥{p.price}</p>
                  <span className="bg-rose-500 text-white px-1.5 py-0.5 rounded-full" style={{ fontSize: '7px' }}>购买</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Right Panel: Property Editors ───────────────────────────────────────────

const BannerEditor = ({ config, setConfig }: { config: HomeConfig; setConfig: React.Dispatch<React.SetStateAction<HomeConfig>> }) => {
  const [activeBannerIdx, setActiveBannerIdx] = useState(0);

  const updateBanner = (idx: number, field: keyof BannerItem, val: string) => {
    setConfig((prev) => {
      const banners = [...prev.banners];
      banners[idx] = { ...banners[idx], [field]: val };
      return { ...prev, banners };
    });
  };

  const addBanner = () => {
    if (config.banners.length >= 5) return;
    setConfig((prev) => ({
      ...prev,
      banners: [...prev.banners, {
        id: Date.now().toString(), tag: '新标签', title: '标题文字',
        subtitle: '副标题描述', cta: '立即查看', image: '', productId: '',
      }],
    }));
    setActiveBannerIdx(config.banners.length);
  };

  const removeBanner = (idx: number) => {
    if (config.banners.length <= 1) return;
    setConfig((prev) => ({ ...prev, banners: prev.banners.filter((_, i) => i !== idx) }));
    setActiveBannerIdx(Math.max(0, idx - 1));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {config.banners.map((b, i) => (
          <button
            key={b.id}
            onClick={() => setActiveBannerIdx(i)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
              activeBannerIdx === i ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Banner {i + 1}
          </button>
        ))}
        {config.banners.length < 5 && (
          <button
            onClick={addBanner}
            className="px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer bg-gray-50 text-gray-400 hover:bg-gray-100 border border-dashed border-gray-300 whitespace-nowrap"
          >
            + 添加
          </button>
        )}
      </div>
      {config.banners[activeBannerIdx] && (
        <div>
          <FieldRow label="标签文字">
            <TextInput value={config.banners[activeBannerIdx].tag} onChange={(v) => updateBanner(activeBannerIdx, 'tag', v)} placeholder="如：明星产品" />
          </FieldRow>
          <FieldRow label="主标题">
            <TextInput value={config.banners[activeBannerIdx].title} onChange={(v) => updateBanner(activeBannerIdx, 'title', v)} placeholder="支持换行，用 \n 分隔" multiline />
          </FieldRow>
          <FieldRow label="副标题">
            <TextInput value={config.banners[activeBannerIdx].subtitle} onChange={(v) => updateBanner(activeBannerIdx, 'subtitle', v)} placeholder="简短描述" />
          </FieldRow>
          <FieldRow label="按钮文字">
            <TextInput value={config.banners[activeBannerIdx].cta} onChange={(v) => updateBanner(activeBannerIdx, 'cta', v)} placeholder="如：立即兑换" />
          </FieldRow>
          <FieldRow label="商品 ID">
            <TextInput value={config.banners[activeBannerIdx].productId} onChange={(v) => updateBanner(activeBannerIdx, 'productId', v)} placeholder="点击按钮跳转的商品 ID" />
          </FieldRow>
          <FieldRow label="背景图片">
            <div className="space-y-2">
              {config.banners[activeBannerIdx].image && (
                <div className="rounded-lg overflow-hidden" style={{ height: '100px' }}>
                  <img src={config.banners[activeBannerIdx].image} alt="preview" className="w-full h-full object-cover object-top" />
                </div>
              )}
              <ImageUploader
                value={config.banners[activeBannerIdx].image}
                onUpload={(url) => updateBanner(activeBannerIdx, 'image', url)}
                variant="button"
                placeholder={config.banners[activeBannerIdx].image ? '重新上传' : '上传背景图片'}
                folder="banners"
              />
            </div>
          </FieldRow>
          {config.banners.length > 1 && (
            <button onClick={() => removeBanner(activeBannerIdx)} className="text-xs text-rose-500 hover:text-rose-600 cursor-pointer flex items-center gap-1 whitespace-nowrap mt-2">
              <i className="ri-delete-bin-line" /> 删除此 Banner
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PointsCardEditor = ({ config, setConfig }: { config: HomeConfig; setConfig: React.Dispatch<React.SetStateAction<HomeConfig>> }) => (
  <div>
    <p className="text-xs text-gray-400 mb-4">选择积分卡片的背景渐变色</p>
    <div className="space-y-2">
      {GRADIENT_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => setConfig((p) => ({ ...p, pointsCardGradient: opt.value }))}
          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
            config.pointsCardGradient === opt.value ? 'border-rose-500 bg-rose-50' : 'border-gray-100 hover:border-gray-200'
          }`}
        >
          <div className="w-8 h-8 rounded-lg flex-shrink-0" style={{ background: `linear-gradient(135deg, ${opt.from}, ${opt.to})` }} />
          <span className="text-sm font-medium text-gray-700">{opt.label}</span>
          {config.pointsCardGradient === opt.value && <i className="ri-check-line text-rose-500 ml-auto" />}
        </button>
      ))}
    </div>
  </div>
);

const BenefitEditor = ({ config, setConfig }: { config: HomeConfig; setConfig: React.Dispatch<React.SetStateAction<HomeConfig>> }) => {
  const updateBenefit = (idx: number, field: keyof BenefitItem, val: string) => {
    setConfig((prev) => {
      const benefits = [...prev.benefits];
      benefits[idx] = { ...benefits[idx], [field]: val };
      return { ...prev, benefits };
    });
  };

  return (
    <div>
      <p className="text-xs text-gray-400 mb-4">最多 6 个品牌亮点</p>
      <div className="space-y-3">
        {config.benefits.map((b, idx) => (
          <div key={idx} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-lg">
                  <i className={`${b.icon} text-emerald-600 text-sm`} />
                </div>
                <span className="text-xs font-medium text-gray-700">{b.title || '未命名'}</span>
              </div>
              {config.benefits.length > 1 && (
                <button onClick={() => setConfig((p) => ({ ...p, benefits: p.benefits.filter((_, i) => i !== idx) }))} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-rose-500 cursor-pointer">
                  <i className="ri-close-line text-sm" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <p className="text-xs text-gray-400 mb-1">标题</p>
                <TextInput value={b.title} onChange={(v) => updateBenefit(idx, 'title', v)} placeholder="亮点标题" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">描述</p>
                <TextInput value={b.desc} onChange={(v) => updateBenefit(idx, 'desc', v)} placeholder="简短描述" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-1.5">图标</p>
            <div className="flex flex-wrap gap-1.5">
              {ICON_OPTIONS.map((icon) => (
                <button key={icon} onClick={() => updateBenefit(idx, 'icon', icon)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all ${b.icon === icon ? 'bg-rose-500 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:border-rose-300'}`}>
                  <i className={`${icon} text-sm`} />
                </button>
              ))}
            </div>
          </div>
        ))}
        {config.benefits.length < 6 && (
          <button onClick={() => setConfig((p) => ({ ...p, benefits: [...p.benefits, { icon: 'ri-star-line', title: '新亮点', desc: '描述文字' }] }))}
            className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-rose-300 hover:text-rose-400 cursor-pointer transition-all whitespace-nowrap">
            + 添加亮点
          </button>
        )}
      </div>
    </div>
  );
};

const CategoryEditor = ({ config, setConfig }: { config: HomeConfig; setConfig: React.Dispatch<React.SetStateAction<HomeConfig>> }) => {
  const updateCategory = (idx: number, field: keyof CategoryItem, val: string) => {
    setConfig((prev) => {
      const categories = [...prev.categories];
      categories[idx] = { ...categories[idx], [field]: val };
      return { ...prev, categories };
    });
  };

  return (
    <div>
      <p className="text-xs text-gray-400 mb-4">最多 8 个分类</p>
      <div className="space-y-3">
        {config.categories.map((cat, idx) => (
          <div key={idx} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${cat.color}`}>
                  <i className={`${cat.icon} ${cat.iconColor} text-sm`} />
                </div>
                <span className="text-xs font-medium text-gray-700">{cat.label || '未命名'}</span>
              </div>
              {config.categories.length > 1 && (
                <button onClick={() => setConfig((p) => ({ ...p, categories: p.categories.filter((_, i) => i !== idx) }))} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-rose-500 cursor-pointer">
                  <i className="ri-close-line text-sm" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <p className="text-xs text-gray-400 mb-1">名称</p>
                <TextInput value={cat.label} onChange={(v) => updateCategory(idx, 'label', v)} placeholder="分类名称" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">跳转路径</p>
                <TextInput value={cat.path} onChange={(v) => updateCategory(idx, 'path', v)} placeholder="/shop?category=xxx" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-1.5">图标</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {ICON_OPTIONS.map((icon) => (
                <button key={icon} onClick={() => updateCategory(idx, 'icon', icon)}
                  className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer transition-all ${cat.icon === icon ? 'bg-rose-500 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:border-rose-300'}`}>
                  <i className={`${icon} text-sm`} />
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mb-1.5">颜色</p>
            <div className="flex flex-wrap gap-1.5">
              {COLOR_PAIRS.map((cp) => (
                <button key={cp.color} onClick={() => { updateCategory(idx, 'color', cp.color); updateCategory(idx, 'iconColor', cp.iconColor); }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer border transition-all ${cat.color === cp.color ? 'border-rose-400 bg-rose-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className={`w-3 h-3 rounded ${cp.color}`} />
                  <span className="text-xs text-gray-600">{cp.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
        {config.categories.length < 8 && (
          <button onClick={() => setConfig((p) => ({ ...p, categories: [...p.categories, { icon: 'ri-store-2-line', label: '新分类', color: 'bg-gray-100', iconColor: 'text-gray-500', path: '/shop' }] }))}
            className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-rose-300 hover:text-rose-400 cursor-pointer transition-all whitespace-nowrap">
            + 添加分类
          </button>
        )}
      </div>
    </div>
  );
};

const MidBannerEditor = ({ config, setConfig }: { config: HomeConfig; setConfig: React.Dispatch<React.SetStateAction<HomeConfig>> }) => (
  <div>
    <FieldRow label="标签">
      <TextInput value={config.midBanner.tag} onChange={(v) => setConfig((p) => ({ ...p, midBanner: { ...p.midBanner, tag: v } }))} placeholder="如：限时特惠" />
    </FieldRow>
    <FieldRow label="标题">
      <TextInput value={config.midBanner.title} onChange={(v) => setConfig((p) => ({ ...p, midBanner: { ...p.midBanner, title: v } }))} placeholder="海报主标题" />
    </FieldRow>
    <FieldRow label="副标题">
      <TextInput value={config.midBanner.subtitle} onChange={(v) => setConfig((p) => ({ ...p, midBanner: { ...p.midBanner, subtitle: v } }))} placeholder="海报副标题" />
    </FieldRow>
    <FieldRow label="跳转路径">
      <TextInput value={config.midBanner.path} onChange={(v) => setConfig((p) => ({ ...p, midBanner: { ...p.midBanner, path: v } }))} placeholder="/shop?category=xxx" />
    </FieldRow>
    <FieldRow label="背景图片">
      <div className="space-y-2">
        {config.midBanner.image && (
          <div className="rounded-lg overflow-hidden" style={{ height: '80px' }}>
            <img src={config.midBanner.image} alt="preview" className="w-full h-full object-cover object-top" />
          </div>
        )}
        <ImageUploader
          value={config.midBanner.image}
          onUpload={(url) => setConfig((p) => ({ ...p, midBanner: { ...p.midBanner, image: url } }))}
          variant="button"
          placeholder={config.midBanner.image ? '重新上传' : '上传背景图片'}
          folder="banners"
        />
      </div>
    </FieldRow>
  </div>
);

const BottomBannerEditor = ({ config, setConfig }: { config: HomeConfig; setConfig: React.Dispatch<React.SetStateAction<HomeConfig>> }) => (
  <div>
    <FieldRow label="标题">
      <TextInput value={config.bottomBanner.title} onChange={(v) => setConfig((p) => ({ ...p, bottomBanner: { ...p.bottomBanner, title: v } }))} placeholder="海报主标题" />
    </FieldRow>
    <FieldRow label="副标题">
      <TextInput value={config.bottomBanner.subtitle} onChange={(v) => setConfig((p) => ({ ...p, bottomBanner: { ...p.bottomBanner, subtitle: v } }))} placeholder="海报副标题" />
    </FieldRow>
    <FieldRow label="跳转路径">
      <TextInput value={config.bottomBanner.path} onChange={(v) => setConfig((p) => ({ ...p, bottomBanner: { ...p.bottomBanner, path: v } }))} placeholder="/community" />
    </FieldRow>
    <FieldRow label="背景图片">
      <div className="space-y-2">
        {config.bottomBanner.image && (
          <div className="rounded-lg overflow-hidden" style={{ height: '80px' }}>
            <img src={config.bottomBanner.image} alt="preview" className="w-full h-full object-cover object-top" />
          </div>
        )}
        <ImageUploader
          value={config.bottomBanner.image}
          onUpload={(url) => setConfig((p) => ({ ...p, bottomBanner: { ...p.bottomBanner, image: url } }))}
          variant="button"
          placeholder={config.bottomBanner.image ? '重新上传' : '上传背景图片'}
          folder="banners"
        />
      </div>
    </FieldRow>
  </div>
);

const ThemeEditor = ({ config, setConfig }: { config: HomeConfig; setConfig: React.Dispatch<React.SetStateAction<HomeConfig>> }) => (
  <div>
    <FieldRow label="店铺名称">
      <TextInput value={config.storeName} onChange={(v) => setConfig((p) => ({ ...p, storeName: v }))} placeholder="请输入店铺名称" />
    </FieldRow>

    <div className="mb-4">
      <p className="text-xs text-gray-500 font-medium mb-2">Logo 图片</p>
      <div className="flex items-center gap-3">
        {config.logoImage && (
          <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
            <img src={config.logoImage} alt="logo preview" className="w-full h-full object-cover object-top" />
          </div>
        )}
        <ImageUploader
          value={config.logoImage}
          onUpload={(url) => setConfig((p) => ({ ...p, logoImage: url }))}
          variant="button"
          placeholder={config.logoImage ? '重新上传' : '上传 Logo'}
          folder="logos"
        />
        {config.logoImage && (
          <button
            onClick={() => setConfig((p) => ({ ...p, logoImage: '' }))}
            className="text-xs text-rose-400 hover:text-rose-500 cursor-pointer whitespace-nowrap"
          >
            清除
          </button>
        )}
      </div>
    </div>

    {!config.logoImage && (
      <div className="mb-4">
        <p className="text-xs text-gray-500 font-medium mb-2">Logo 背景色（文字模式）</p>
        <div className="grid grid-cols-3 gap-2">
          {LOGO_GRADIENT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setConfig((p) => ({ ...p, logoBgGradient: opt.value }))}
              className={`flex items-center gap-1.5 p-2 rounded-lg border cursor-pointer transition-all ${
                config.logoBgGradient === opt.value ? 'border-rose-500 bg-rose-50' : 'border-gray-100 hover:border-gray-200'
              }`}
            >
              <div className="w-5 h-5 rounded-full flex-shrink-0" style={{ background: `linear-gradient(135deg, ${opt.from}, ${opt.to})` }} />
              <span className="text-xs text-gray-600">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>
    )}

    <p className="text-xs text-gray-500 font-medium mb-2">主题颜色</p>
    <div className="grid grid-cols-2 gap-2 mb-4">
      {THEME_OPTIONS.map((opt) => (
        <button key={opt.value} onClick={() => setConfig((p) => ({ ...p, themeColor: opt.value }))}
          className={`flex items-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer transition-all ${config.themeColor === opt.value ? 'border-rose-500 bg-rose-50' : 'border-gray-100 hover:border-gray-200'}`}>
          <div className="w-6 h-6 rounded-lg flex-shrink-0" style={{ background: `linear-gradient(135deg, ${opt.from}, ${opt.to})` }} />
          <span className="text-xs font-medium text-gray-700">{opt.label}</span>
          {config.themeColor === opt.value && <i className="ri-check-line text-rose-500 ml-auto text-sm" />}
        </button>
      ))}
    </div>
  </div>
);

// ─── Product Section Editor ───────────────────────────────────────────────────

const ProductSectionEditor = ({
  compId,
  config,
  setConfig,
  allProducts,
}: {
  compId: string;
  config: HomeConfig;
  setConfig: React.Dispatch<React.SetStateAction<HomeConfig>>;
  allProducts: DBProduct[];
}) => {
  const [showPicker, setShowPicker] = useState(false);

  const sections = config.productSections || [];
  const section = sections.find((s) => s.id === compId);

  // Auto-create section data if not exists
  useEffect(() => {
    if (!section) {
      setConfig((p) => ({
        ...p,
        productSections: [
          ...(p.productSections || []),
          { id: compId, title: '精选商品', subtitle: '', displayStyle: 'grid', productIds: [] },
        ],
      }));
    }
  }, [compId, section, setConfig]);

  if (!section) return null;

  const updateSection = (field: keyof ProductSectionItem, val: string | string[]) => {
    setConfig((p) => ({
      ...p,
      productSections: (p.productSections || []).map((s) =>
        s.id === compId ? { ...s, [field]: val } : s
      ),
    }));
  };

  const selectedProducts = section.productIds
    .map((id) => allProducts.find((p) => p.id === id))
    .filter(Boolean) as DBProduct[];

  return (
    <div>
      <FieldRow label="区块标题">
        <TextInput value={section.title} onChange={(v) => updateSection('title', v)} placeholder="如：精选商品、爆款推荐" />
      </FieldRow>
      <FieldRow label="副标题（可选）">
        <TextInput value={section.subtitle} onChange={(v) => updateSection('subtitle', v)} placeholder="简短描述" />
      </FieldRow>

      <FieldRow label="展示样式">
        <div className="flex gap-2">
          <button
            onClick={() => updateSection('displayStyle', 'grid')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 cursor-pointer transition-all ${
              section.displayStyle === 'grid' ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <i className="ri-grid-line text-sm" />
            <span className="text-xs font-medium">网格</span>
          </button>
          <button
            onClick={() => updateSection('displayStyle', 'list')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 cursor-pointer transition-all ${
              section.displayStyle === 'list' ? 'border-rose-500 bg-rose-50 text-rose-600' : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <i className="ri-list-check text-sm" />
            <span className="text-xs font-medium">列表</span>
          </button>
        </div>
      </FieldRow>

      <FieldRow label={`已选商品 (${selectedProducts.length})`}>
        <div className="space-y-2">
          {selectedProducts.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {selectedProducts.map((p, idx) => (
                <div key={p.id} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                  <span className="text-xs text-gray-400 w-4 flex-shrink-0">{idx + 1}</span>
                  <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                    {p.images?.[0] && (
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700 truncate">{p.name}</p>
                    <p className="text-xs text-rose-500 font-medium">¥{p.price}</p>
                  </div>
                  <button
                    onClick={() => updateSection('productIds', section.productIds.filter((id) => id !== p.id))}
                    className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-rose-500 cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={() => setShowPicker(true)}
            className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-rose-300 hover:text-rose-400 cursor-pointer transition-all flex items-center justify-center gap-1.5 whitespace-nowrap"
          >
            <i className="ri-add-line" />
            {selectedProducts.length > 0 ? '修改选品' : '选择商品'}
          </button>
        </div>
      </FieldRow>

      {showPicker && (
        <ProductPickerModal
          selectedIds={section.productIds}
          onConfirm={(ids) => updateSection('productIds', ids)}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AdminHomeDesignPage = () => {
  const [config, setConfig] = useState<HomeConfig>(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [allProducts, setAllProducts] = useState<DBProduct[]>([]);
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);

  // Drag state
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);

  const layout = config.pageLayout ?? DEFAULT_LAYOUT;

  const setLayout = (newLayout: PageComponent[]) => {
    setConfig((p) => ({ ...p, pageLayout: newLayout }));
  };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    const [configRes, productsRes, annRes] = await Promise.all([
      supabase.from('admin_settings').select('value').eq('key', 'home_config').maybeSingle(),
      supabase.from('products').select('id, name, price, original_price, images, category, sales').eq('status', '上架').order('sort_order', { ascending: true }),
      fetch(ANNOUNCEMENT_EDGE_URL).then((r) => r.json()).catch(() => ({ data: [] })),
    ]);

    setAllProducts((productsRes.data as DBProduct[]) || []);
    setAnnouncements((annRes.data as AnnouncementItem[]) ?? []);

    if (configRes.data?.value) {
      try {
        const parsed = JSON.parse(configRes.data.value) as HomeConfig;
        const merged = { ...DEFAULT_CONFIG, ...parsed };
        if (!merged.pageLayout || merged.pageLayout.length === 0) {
          merged.pageLayout = DEFAULT_LAYOUT;
        } else {
          merged.pageLayout = ensureFixedComponents(merged.pageLayout);
        }
        if (!merged.productSections) {
          merged.productSections = [];
        }
        setConfig(merged);
      } catch {
        setConfig(DEFAULT_CONFIG);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from('admin_settings')
      .upsert({ key: 'home_config', value: JSON.stringify(config), updated_at: new Date().toISOString() }, { onConflict: 'key' });
    setSaving(false);
    if (!error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  // Add product section component
  const addProductSection = (title = '精选商品', subtitle = '') => {
    const newId = `productSection_${Date.now()}`;
    const newComp: PageComponent = { id: newId, type: 'productSection', visible: true };
    const newSection: ProductSectionItem = { id: newId, title, subtitle, displayStyle: 'grid', productIds: [] };
    setConfig((p) => ({
      ...p,
      pageLayout: [...(p.pageLayout ?? DEFAULT_LAYOUT), newComp],
      productSections: [...(p.productSections || []), newSection],
    }));
    setSelectedId(newId);
  };

  // Remove product section
  const removeProductSection = (id: string) => {
    setConfig((p) => ({
      ...p,
      pageLayout: (p.pageLayout ?? DEFAULT_LAYOUT).filter((c) => c.id !== id),
      productSections: (p.productSections || []).filter((s) => s.id !== id),
    }));
    if (selectedId === id) setSelectedId(null);
  };

  // Drag handlers
  const handleDragStart = (idx: number) => {
    dragItem.current = idx;
    setDraggingIdx(idx);
  };

  const handleDragEnter = (idx: number) => {
    dragOverItem.current = idx;
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
      const newLayout = [...layout];
      const draggedItem = newLayout.splice(dragItem.current, 1)[0];
      newLayout.splice(dragOverItem.current, 0, draggedItem);
      setLayout(newLayout);
    }
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggingIdx(null);
  };

  const toggleVisible = (id: string) => {
    setLayout(layout.map((c) => c.id === id ? { ...c, visible: !c.visible } : c));
  };

  const selectedComp = layout.find((c) => c.id === selectedId);

  const renderPreviewComponent = (comp: PageComponent) => {
    const isSelected = selectedId === comp.id;
    if (!comp.visible) return null;
    switch (comp.type) {
      case 'banner': return <PreviewBanner key={comp.id} config={config} selected={isSelected} />;
      case 'pointsCard': return <PreviewPointsCard key={comp.id} config={config} selected={isSelected} />;
      case 'benefit': return <PreviewBenefit key={comp.id} config={config} selected={isSelected} />;
      case 'category': return <PreviewCategory key={comp.id} config={config} selected={isSelected} />;
      case 'midBanner': return <PreviewMidBanner key={comp.id} config={config} selected={isSelected} />;
      case 'bottomBanner': return <PreviewBottomBanner key={comp.id} config={config} selected={isSelected} />;
      case 'announcement': return <PreviewAnnouncement key={comp.id} announcements={announcements} selected={isSelected} />;
      case 'productSection': {
        const section = (config.productSections || []).find((s) => s.id === comp.id);
        if (!section) return null;
        return (
          <PreviewProductSection
            key={comp.id}
            section={section}
            allProducts={allProducts}
            selected={isSelected}
          />
        );
      }
      default: return null;
    }
  };

  const renderEditor = () => {
    if (!selectedComp) {
      return (
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-2xl mb-3">
            <i className="ri-cursor-line text-gray-400 text-xl" />
          </div>
          <p className="text-sm font-medium text-gray-500">点击中间预览区的组件</p>
          <p className="text-xs text-gray-400 mt-1">选中后在此编辑属性</p>
        </div>
      );
    }
    const meta = COMPONENT_META[selectedComp.type];
    return (
      <div>
        <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${meta.color.split(' ')[0]}`}>
            <i className={`${meta.icon} ${meta.color.split(' ')[1]} text-base`} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">{meta.label}</p>
            <p className="text-xs text-gray-400">{meta.desc}</p>
          </div>
          {selectedComp.type === 'productSection' && (
            <button
              onClick={() => removeProductSection(selectedComp.id)}
              className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-rose-500 cursor-pointer rounded-lg hover:bg-rose-50 transition-all"
              title="删除此商品区块"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          )}
        </div>
        {selectedComp.type === 'banner' && <BannerEditor config={config} setConfig={setConfig} />}
        {selectedComp.type === 'pointsCard' && <PointsCardEditor config={config} setConfig={setConfig} />}
        {selectedComp.type === 'benefit' && <BenefitEditor config={config} setConfig={setConfig} />}
        {selectedComp.type === 'category' && <CategoryEditor config={config} setConfig={setConfig} />}
        {selectedComp.type === 'midBanner' && <MidBannerEditor config={config} setConfig={setConfig} />}
        {selectedComp.type === 'bottomBanner' && <BottomBannerEditor config={config} setConfig={setConfig} />}
        {selectedComp.type === 'productSection' && (
          <ProductSectionEditor
            compId={selectedComp.id}
            config={config}
            setConfig={setConfig}
            allProducts={allProducts}
          />
        )}
        {selectedComp.type === 'announcement' && (
          <AnnouncementEditor />
        )}
        {(selectedComp.type === 'banner' || selectedComp.type === 'pointsCard') && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-2">主题颜色</p>
            <div className="grid grid-cols-3 gap-2">
              {THEME_OPTIONS.map((opt) => (
                <button key={opt.value} onClick={() => setConfig((p) => ({ ...p, themeColor: opt.value }))}
                  className={`flex items-center gap-1.5 p-2 rounded-lg border cursor-pointer transition-all ${config.themeColor === opt.value ? 'border-rose-500 bg-rose-50' : 'border-gray-100 hover:border-gray-200'}`}
                >
                  <div className="w-5 h-5 rounded flex-shrink-0" style={{ background: `linear-gradient(135deg, ${opt.from}, ${opt.to})` }} />
                  <span className="text-xs text-gray-600">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout title="首页装修">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex gap-0 h-full" style={{ minHeight: 'calc(100vh - 120px)' }}>

          {/* ── Left: Component List ── */}
          <div className="w-52 flex-shrink-0 bg-white rounded-xl mr-4 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">页面组件</h3>
              <p className="text-xs text-gray-400 mt-0.5">拖拽调整顺序</p>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {layout.map((comp, idx) => {
                const meta = COMPONENT_META[comp.type];
                const isSelected = selectedId === comp.id;
                const isProductSection = comp.type === 'productSection';
                const sectionData = isProductSection
                  ? (config.productSections || []).find((s) => s.id === comp.id)
                  : null;
                return (
                  <div
                    key={comp.id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => setSelectedId(comp.id === selectedId ? null : comp.id)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all select-none ${
                      isSelected
                        ? 'bg-rose-50 border border-rose-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    } ${!comp.visible ? 'opacity-40' : ''} ${draggingIdx === idx ? 'opacity-50 scale-95' : ''}`}
                  >
                    <i className="ri-drag-move-2-line text-gray-300 text-base flex-shrink-0" />
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${meta.color.split(' ')[0]}`}>
                      <i className={`${meta.icon} ${meta.color.split(' ')[1]} text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium truncate ${isSelected ? 'text-rose-600' : 'text-gray-700'}`}>
                        {isProductSection && sectionData ? sectionData.title : meta.label}
                      </p>
                      {isProductSection && sectionData && (
                        <p className="text-xs text-gray-400 truncate">{sectionData.productIds.length} 件商品</p>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleVisible(comp.id); }}
                      className={`w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${comp.visible ? 'text-gray-400 hover:text-gray-600' : 'text-gray-300 hover:text-gray-500'}`}
                    >
                      <i className={`${comp.visible ? 'ri-eye-line' : 'ri-eye-off-line'} text-sm`} />
                    </button>
                  </div>
                );
              })}
            </div>
            {/* Add product section button */}
            <div className="p-3 border-t border-gray-100 space-y-1.5">
              <p className="text-xs text-gray-400 font-medium mb-2">+ 添加商品展示</p>
              <button
                onClick={() => addProductSection('爆款产品', '热销爆款 · 好评如潮')}
                className="w-full py-2 border-2 border-dashed border-orange-200 rounded-xl text-xs text-orange-400 hover:border-orange-400 hover:text-orange-500 cursor-pointer transition-all flex items-center justify-center gap-1.5 whitespace-nowrap"
              >
                <i className="ri-fire-line" />
                爆款产品
              </button>
              <button
                onClick={() => addProductSection('新品上市', '最新上架 · 抢先体验')}
                className="w-full py-2 border-2 border-dashed border-emerald-200 rounded-xl text-xs text-emerald-500 hover:border-emerald-400 hover:text-emerald-600 cursor-pointer transition-all flex items-center justify-center gap-1.5 whitespace-nowrap"
              >
                <i className="ri-sparkling-line" />
                新品上市
              </button>
              <button
                onClick={() => addProductSection('精选商品', '')}
                className="w-full py-2 border-2 border-dashed border-pink-200 rounded-xl text-xs text-pink-400 hover:border-pink-400 hover:text-pink-500 cursor-pointer transition-all flex items-center justify-center gap-1.5 whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line" />
                自定义展示
              </button>
              <p className="text-xs text-gray-400 text-center pt-1">{layout.filter(c => c.visible).length} 个组件显示中</p>
            </div>
          </div>

          {/* ── Center: Phone Preview ── */}
          <div className="flex-1 flex flex-col items-center">
            <div className="flex items-center justify-between w-full mb-3 px-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-rose-400" />
                <span className="text-xs text-gray-500">点击组件可选中编辑</span>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                  saved ? 'bg-emerald-500 text-white' : 'bg-rose-500 hover:bg-rose-600 text-white'
                } disabled:opacity-60`}
              >
                {saving ? (
                  <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> 保存中</>
                ) : saved ? (
                  <><i className="ri-check-line" /> 已保存</>
                ) : (
                  <><i className="ri-save-line" /> 保存配置</>
                )}
              </button>
            </div>

            {/* Phone frame */}
            <div className="bg-gray-900 rounded-3xl p-2.5 shadow-xl" style={{ width: '300px' }}>
              <div className="flex items-center justify-center mb-1">
                <div className="w-16 h-1.5 bg-gray-700 rounded-full" />
              </div>
              <div className="bg-white rounded-2xl overflow-hidden" style={{ height: '580px' }}>
                {/* Status bar */}
                <div className="bg-gray-800 px-3 py-2 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-4 h-4 rounded-full overflow-hidden flex-shrink-0 ${!config.logoImage ? `bg-gradient-to-br ${config.logoBgGradient}` : ''}`}>
                      {config.logoImage ? (
                        <img src={config.logoImage} alt="logo" className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-white font-bold" style={{ fontSize: '7px' }}>
                            {config.storeName?.charAt(0) ?? '我'}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="text-white text-xs font-bold">{config.storeName}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <i className="ri-search-line text-white text-xs" />
                    <i className="ri-shopping-cart-line text-white text-xs" />
                  </div>
                </div>

                {/* Scrollable content */}
                <div className="overflow-y-auto h-full pb-4" style={{ scrollbarWidth: 'none' }}>
                  {layout.map((comp) => {
                    if (!comp.visible) return null;
                    const isSelected = selectedId === comp.id;
                    return (
                      <div
                        key={comp.id}
                        onClick={() => setSelectedId(comp.id === selectedId ? null : comp.id)}
                        className={`cursor-pointer transition-all ${isSelected ? 'outline outline-2 outline-rose-500 outline-offset-[-2px]' : 'hover:outline hover:outline-1 hover:outline-rose-300 hover:outline-offset-[-1px]'}`}
                      >
                        {renderPreviewComponent(comp)}
                      </div>
                    );
                  })}
                  <div style={{ height: '60px' }} />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">保存后前台首页立即生效</p>
          </div>

          {/* ── Right: Property Panel ── */}
          <div className="w-72 flex-shrink-0 bg-white rounded-xl ml-4 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">属性设置</h3>
              {selectedId && (
                <button onClick={() => setSelectedId(null)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-base" />
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {!selectedId && (
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <p className="text-xs text-gray-500 font-semibold mb-3 flex items-center gap-1.5">
                    <i className="ri-settings-3-line text-gray-400" />
                    全局设置
                  </p>
                  <ThemeEditor config={config} setConfig={setConfig} />
                </div>
              )}
              {renderEditor()}
            </div>
          </div>

        </div>
      )}
    </AdminLayout>
  );
};

export default AdminHomeDesignPage;
